<template>
  <Modal
    ref="modal"
    :content-class="['dinamic-modal', contentClass]"
    :content-right-class="['dinamic-modal', contentRightClass]"
    :dual-panels="isRightContentActive"
    :split-animation="activeSplitAnimation"
    :split-active="isRightContentActive"
    :show-back-button="showBackButton"
    @return-back="$emit('return-back')"
    @panel-change="onPanelChange"
    @open="resetMobilePanelHeights"
  >
    <template #activator="{ onClick }">
      <slot name="activator" :on-click="onClick" />
    </template>
    <template #content>
      <div
        ref="modal-content"
        class="dinamic-modal__content"
        :class="{
          'dinamic-modal__content--right-only': isMobileRightPanelOnly,
        }"
        :style="panelHeightStyle"
      >
        <div ref="modal-content-inner">
          <template v-if="useMobileSplitStack">
            <Transition
              name="fade-modal-content"
              @enter="enter"
              @before-leave="beforeEnter"
            >
              <div v-if="!isMobileRightPanelOnly" key="mobile-split-left">
                <component
                  :is="activeSplitLeftContent?.component"
                  v-bind="activeSplitLeftContent?.props"
                  @close-left-panel="closeLeftPanel"
                />
              </div>
              <div
                v-else
                key="mobile-split-right"
                ref="modal-content-right-inner"
              >
                <component
                  :is="activeSplitRightContent?.component"
                  v-bind="activeSplitRightContent?.props"
                />
              </div>
            </Transition>
          </template>
          <template v-else>
            <template v-for="content in contentList" :key="content.id">
              <Transition
                :name="content.transitionName"
                @enter="enter"
                @before-leave="beforeEnter"
              >
                <KeepAlive>
                  <component
                    :is="content.component"
                    v-if="activeContent === content.id"
                    v-bind="content.props"
                  />
                </KeepAlive>
              </Transition>
            </template>
          </template>
        </div>
      </div>
    </template>
    <template
      v-if="isRightContentActive && !useMobileSplitStack"
      #content-right
    >
      <div
        class="dinamic-modal__content dinamic-modal__content--right"
        :class="{
          'dinamic-modal__content--right-only': isMobileRightPanelOnly,
        }"
        :style="rightPanelHeightStyle"
      >
        <div ref="modal-content-right-inner">
          <template v-for="content in rightContentList" :key="content.id">
            <component
              :is="content.component"
              v-if="activeContent === content.id"
              v-bind="content.props"
            />
          </template>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  contentList: {
    type: Array,
    default: () => [],
  },
  rightContentList: {
    type: Array,
    default: () => [],
  },
  activeContent: {
    type: String,
    default: null,
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: [String, Array, Object],
    default: '',
  },
  contentRightClass: {
    type: [String, Array, Object],
    default: '',
  },
});

defineEmits(['return-back']);

const isMobile = inject('isMobile');

const modalContentInner = useTemplateRef('modal-content-inner');
const modalContentRightInner = useTemplateRef('modal-content-right-inner');
const modalContentRef = useTemplateRef('modal-content');
const modalRef = useTemplateRef('modal');

const modalContentHeight = ref(0);
const modalRightContentHeight = ref(0);
const isMobileRightPanelOnly = ref(false);

const hasRightContent = computed(() => props.rightContentList.length > 0);

const isRightContentActive = computed(
  () =>
    hasRightContent.value &&
    props.rightContentList.some((item) => item.id === props.activeContent),
);

const activeSplitLeftContent = computed(() =>
  props.contentList.find((item) => item.id === props.activeContent),
);

const activeSplitRightContent = computed(() =>
  props.rightContentList.find((item) => item.id === props.activeContent),
);

const activeSplitAnimation = computed(() => {
  if (!isRightContentActive.value) {
    return false;
  }

  return !!(
    activeSplitLeftContent.value?.splitAnimation ||
    activeSplitRightContent.value?.splitAnimation
  );
});

const useMobileSplitStack = computed(
  () =>
    isMobile.value && isRightContentActive.value && activeSplitAnimation.value,
);

function buildHeightStyle(heightValue) {
  if (!heightValue) {
    return {};
  }

  const height = heightValue > 68 ? heightValue : 68;

  return {
    height: `${height}px`,
    maxHeight: `${height}px`,
  };
}

const panelHeightStyle = computed(() => {
  const usesSplitAspectRatio = activeSplitAnimation.value && !isMobile.value;

  if (usesSplitAspectRatio) {
    return {};
  }

  const height = isMobileRightPanelOnly.value
    ? modalRightContentHeight.value || modalContentHeight.value
    : modalContentHeight.value;

  return buildHeightStyle(height);
});

const rightPanelHeightStyle = computed(() => panelHeightStyle.value);

function resetMobilePanelHeights() {
  isMobileRightPanelOnly.value = false;
  modalContentHeight.value = 0;
  modalRightContentHeight.value = 0;
}

function onPanelChange({ showLeft, showRight }) {
  if (!isMobile.value || !activeSplitAnimation.value) {
    return;
  }

  isMobileRightPanelOnly.value = !showLeft && showRight;

  if (!isMobileRightPanelOnly.value) {
    modalRightContentHeight.value = 0;
  }
}

function beforeEnter() {
  modalContentHeight.value = modalContentRef.value?.offsetHeight;
}

function enter(element) {
  const height = element?.offsetHeight;

  if (!height) {
    return;
  }

  if (isMobileRightPanelOnly.value) {
    modalRightContentHeight.value = height;
  } else {
    modalContentHeight.value = height;
  }
}

function closeLeftPanel() {
  modalRef.value?.closeLeftContent?.();
}

useResizeObserver(modalContentInner, (entries) => {
  if (isMobileRightPanelOnly.value) {
    return;
  }

  const entry = entries[0];
  modalContentHeight.value = entry.contentRect.height
    ? entry.contentRect.height
    : modalContentHeight.value;
});

useResizeObserver(modalContentRightInner, (entries) => {
  if (!isMobileRightPanelOnly.value) {
    return;
  }

  const entry = entries[0];
  modalRightContentHeight.value = entry.contentRect.height
    ? entry.contentRect.height
    : modalRightContentHeight.value;
});

defineExpose({
  modalRef,
  open: () => {
    modalRef.value?.open?.();
  },
  close: () => {
    modalRef.value?.close?.();
  },
});
</script>

<style lang="scss" scoped>
.dinamic-modal {
  &__content {
    position: relative;
    overflow: hidden;
    transition:
      height 0.3s ease,
      max-height 0.3s ease;

    &--right {
      height: 100%;
    }

    &--right-only {
      height: auto;
    }

    :deep(.fade-modal-content-leave-active) {
      width: 100%;
    }
  }
}
</style>
